import { useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';

type Map = Record<string, unknown>;

type Fields<M extends Map> = Record<keyof M, { id: string; name: string }>;

type Result<M extends Map> = {
  [K in keyof M]: { id: string; name: string; value: M[K] };
};

export const useContextualizedFields = <M extends Map>(
  fields: Fields<M>,
  context?: string | null,
): Result<M> => {
  const { values } = useFormikContext();

  const contextualizedFields = useMemo(
    () =>
      Object.keys(fields).reduce((acc, key) => {
        const { field } = ensureDefined({ field: fields[key] });

        const fieldName = `${context ? `${context}.` : EMPTY_STRING}${field.name}`;
        const id = `${context ? `${context}-` : EMPTY_STRING}${field.id}`;

        return {
          ...acc,
          [key]: {
            name: fieldName,
            value: getIn(values, fieldName) as M[keyof M],
            id,
          },
        };
      }, EMPTY_OBJECT as Result<M>),
    [context, fields, values],
  );

  return contextualizedFields;
};
